/**
 * SPA mirror of marketing/lib/ad-consent.ts (CCPA/CPRA "Do Not Sell or
 * Share" + GPC honoring).
 *
 * The opt-out cookie (`fk_ad_optout`) is written by the marketing site's
 * "Do Not Sell or Share My Personal Information" footer control, scoped to
 * `.franklyadvisors.com` — so it's already visible here after the
 * marketing -> app subdomain hop. This module only needs to read it (plus
 * the live `navigator.globalPrivacyControl` signal); the SPA has no opt-out
 * UI of its own.
 *
 * `isAdSharingSuppressed()` is the single predicate every SPA Meta Pixel /
 * CAPI fire site (src/lib/meta-pixel.ts, src/hooks/useMetaCapi.ts,
 * src/lib/meta-capi.ts) must consult before firing.
 */

const OPT_OUT_COOKIE = 'fk_ad_optout';

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** True when the browser sends a Global Privacy Control signal. */
export function hasGpcSignal(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

/** True when the visitor opted out via the marketing site's Do-Not-Sell control. */
export function hasManualOptOut(): boolean {
  return getCookie(OPT_OUT_COOKIE) === '1';
}

/**
 * The single predicate every SPA pixel/CAPI fire site must consult before
 * it fires. Suppressed when either a GPC signal is present or the visitor
 * has manually opted out.
 */
export function isAdSharingSuppressed(): boolean {
  return hasGpcSignal() || hasManualOptOut();
}
